import type { Country, DeliveryZoneCode } from '@noths/polaris-client-localisation';
import {
  countries,
  deliveryZoneCodeValues,
  ukDeliveryZones,
} from '@noths/polaris-client-localisation';
import type { DropDownOption } from '@noths/polaris-client-ribbons-design-system';
import { createSelector } from '@reduxjs/toolkit';
import { groupBy as RgroupBy } from 'ramda';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { DecoratedDeliveryOption, GroupedCountries } from 'src/redux/delivery/types';
import { getMinMaxDeliveryEstimate } from './utils/getMinMaxDeliveryEstimate';

const UK_DROPDOWN_OPTIONS_LABEL = 'United Kingdom';
const OTHER_COUNTRIES_DROPDOWN_OPTIONS_LABEL = 'Other countries';

export const selectSelectedZone = ({ delivery }: ReduxApplicationState) => delivery.selectedZone;

export const selectSelectedCountry = ({ delivery }: ReduxApplicationState) =>
  delivery.selectedCountry;

export const selectDeliveryOptions = ({ delivery }: ReduxApplicationState) => delivery.options;

export const selectDeliveryOptionsForZone = createSelector(
  [selectSelectedZone, selectDeliveryOptions],
  (selectedZone, options) =>
    options.find(({ zoneCode }) => zoneCode === selectedZone)?.deliveryOptions,
);

const selectUserCurrencyCode = (state: ReduxApplicationState) => {
  return state.user?.currencyCode;
};

export const selectDecoratedDeliveryOptions = createSelector(
  [selectDeliveryOptionsForZone, selectUserCurrencyCode],
  (deliveryOptionsForZone, userCurrency = 'GBP'): DecoratedDeliveryOption[] | [] => {
    const deliveryOptions =
      (deliveryOptionsForZone?.map(
        ({
          cutoffDateTime,
          date,
          deliveryProvider,
          exactDate,
          expressShippingPromotion,
          freePartnerShipping,
          max,
          min,
          optionCode,
          prices,
        }) => ({
          cutoffDateTime,
          date,
          exactDate,
          max,
          min,
          optionCode,
          partnerFreeDeliveryThresholdPrice:
            freePartnerShipping?.thresholdPrices?.find(
              ({ currencyCode }) => currencyCode === userCurrency,
            ) ?? null,
          price: prices.find(({ currencyCode }) => currencyCode === userCurrency)!,
          ...(optionCode === 'EXPRESS_24' &&
            expressShippingPromotion && {
              preSalePrice: expressShippingPromotion.originalDeliveryOption.prices.find(
                ({ currencyCode }) => currencyCode === userCurrency,
              ),
            }),
          providerName: deliveryProvider?.name,
        }),
      ) as DecoratedDeliveryOption[]) ?? [];

    return deliveryOptions;
  },
);

export const selectMinMaxDeliveryEstimates = createSelector(
  [selectDecoratedDeliveryOptions],
  (countryDeliveryOptions) => getMinMaxDeliveryEstimate(countryDeliveryOptions),
);

export const selectProductDeliveryOptions = (state: ReduxApplicationState) =>
  state.delivery.options;

export const selectZonesDeliveredTo = createSelector(
  [selectDeliveryOptions],
  (options): DeliveryZoneCode[] => options.map(({ zoneCode }) => zoneCode),
);

export const selectHasUKDeliveryOnly = createSelector(
  [selectZonesDeliveredTo],
  (zonesDeliveredTo) => !zonesDeliveredTo.some((zoneCode) => !ukDeliveryZones.includes(zoneCode)),
);

export const selectHasSelectedCountriesDeliveryOnly = createSelector(
  [selectHasUKDeliveryOnly, selectZonesDeliveredTo],
  (hasUKDeliveryOnly, zonesDeliveredTo): boolean =>
    !(
      hasUKDeliveryOnly ||
      deliveryZoneCodeValues.every((zoneCode) =>
        zonesDeliveredTo.includes(zoneCode as DeliveryZoneCode),
      )
    ),
);

export const selectCountriesDeliveredTo = createSelector(
  [selectZonesDeliveredTo],
  (zonesDeliveredTo): Country[] =>
    countries.filter(({ deliveryZoneCode }) =>
      zonesDeliveredTo.includes(deliveryZoneCode as DeliveryZoneCode),
    ),
);

const createDropDownOptionFromCountry = ({
  countryCode,
  deliveryZoneCode,
  name,
}: Country): DropDownOption => ({
  optionLabel: name,
  optionValue: `${countryCode}|${deliveryZoneCode}${name === 'United Kingdom' ? `|UK` : ''}`,
});

const isUsersCountry =
  (userCountryCode?: Country['countryCode']) =>
  ({ countryCode }: Country) =>
    countryCode === userCountryCode;

const selectUserCountryCode = (state: ReduxApplicationState) => {
  return state.user?.countryCode;
};

const selectCountriesForDropdown = createSelector(
  [selectCountriesDeliveredTo, selectUserCountryCode],
  (countriesDeliveredTo, userCountryCode): DropDownOption[] => {
    const countryDropDownOptions = countriesDeliveredTo.map(createDropDownOptionFromCountry);

    if (countriesDeliveredTo.some(isUsersCountry(userCountryCode))) {
      return countryDropDownOptions;
    }

    const selectedCountryData = countries.find(isUsersCountry(userCountryCode));

    return (
      selectedCountryData ? [createDropDownOptionFromCountry(selectedCountryData)] : []
    ).concat(countryDropDownOptions);
  },
);

export const selectGroupedCountriesForDropdown = createSelector(
  selectCountriesForDropdown,
  (countriesOptions): GroupedCountries => {
    const groups = RgroupBy<DropDownOption>((country): keyof GroupedCountries => {
      const [countryCode] = country.optionValue.split('|');
      const isUKCountryCode = countryCode === 'GB' || countryCode === 'UK';

      return isUKCountryCode ? UK_DROPDOWN_OPTIONS_LABEL : OTHER_COUNTRIES_DROPDOWN_OPTIONS_LABEL;
    })(countriesOptions);

    return groups as GroupedCountries;
  },
);
